import { scanTokenFromPath, tabFromPath, tabPath, tabs, type Tab } from "../features/shell/navigation.js";
import { DETAIL_ROUTES, detailIdsFromUrl, detailRouteKeysForTab, type DetailRouteKey } from "./detailRoutes.js";
import { FINANCE_DEFAULT_SECTION, financeSectionFromHash, financeSectionPath, type FinanceSection } from "../features/finance/sections.js";
import type { EntityReferenceId } from "./types.js";

// Hand-rolled history routing for the app shell: URL <-> AppRoute mapping,
// tab persistence, and pushState/replaceState navigation. App.tsx owns the
// state; this module owns the URL logic.

export type AppRoute = {
  tab: Tab;
  scanToken: string;
  assetId: EntityReferenceId | null;
  placeId: EntityReferenceId | null;
  productId: EntityReferenceId | null;
  recipeId: EntityReferenceId | null;
  assistantChatId: string | null;
  releaseNotesVersion: string | null;
  /**
   * The detail ids this URL carries in its QUERY string, keyed by detail route
   * (#533). Every field above is a path segment, which is why the shell has to
   * parse it to pick a tab at all; these are the ids that name an object
   * WITHIN a tab — a shopping list, a shopping trip, a chore, a project — and
   * before this field the shell could not see them.
   *
   * That was the last hole in "one URL, one owner": the views wrote these ids
   * and `entityRoutes.ts` published them, but `canonicalPathForRoute` could
   * only ever hand back the bare tab for a route carrying one, so the shell's
   * own answer to "what URL opens this page" silently dropped the object.
   *
   * Only the routes belonging to the resolved tab are read, because a
   * parameter means nothing on a page that does not read it — a stray
   * `?listId=` on `/chores` names no chore and must not survive.
   */
  detailIds: Partial<Record<DetailRouteKey, string>>;
  /**
   * The Finance section the URL hash names (#1681), Overview when it names
   * none. A section is not an object, so it is not a detail route; but it IS
   * part of "what URL opens this page", so the canonical path carries it.
   * Only read on the finance tab — a `#budgets` on `/chores` names nothing.
   */
  financeSection: FinanceSection;
  /**
   * The URL matched nothing this app can name (#1259). `tab` still carries a
   * page so the shell has something to frame, but the view must render the
   * not-found state rather than that page: falling through to the stored tab
   * made one URL render different pages for different people, and left the
   * wrong URL in the address bar to be copied and shared.
   */
  notFound: boolean;
};

const emptyRoute: AppRoute = {
  tab: "dashboard",
  scanToken: "",
  assetId: null,
  placeId: null,
  productId: null,
  recipeId: null,
  assistantChatId: null,
  releaseNotesVersion: null,
  detailIds: {},
  financeSection: FINANCE_DEFAULT_SECTION,
  notFound: false
};

/**
 * An AppRoute with every field defaulted, so adding one does not mean editing
 * every return site.
 *
 * The query-borne detail ids are filled in from the address here rather than at
 * each call site, for the same reason: which of them apply depends only on the
 * tab this route resolved to, and every branch below has already decided that
 * by the time it calls this. A caller may still pass `detailIds` explicitly —
 * the tests do — and an explicit value wins.
 */
function route(partial: Partial<AppRoute>): AppRoute {
  const merged = { ...emptyRoute, ...partial };
  if (merged.tab === "finance" && partial.financeSection === undefined) {
    merged.financeSection = financeSectionFromHash(window.location.hash ?? "") ?? FINANCE_DEFAULT_SECTION;
  }
  if (partial.detailIds) return merged;
  return { ...merged, detailIds: detailIdsFromUrl(merged.tab) };
}

export function isTab(value: string | null): value is Tab {
  return tabs.some((item) => item.id === value);
}

export function assetIdFromPath(pathname: string): EntityReferenceId | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const match = /^\/inventory\/assets\/([^/]+)$/.exec(normalized);
  if (!match) return null;
  return decodeURIComponent(match[1] ?? "");
}

export function assetDetailsPath(assetId: EntityReferenceId): string {
  return `/inventory/assets/${encodeURIComponent(String(assetId))}`;
}

// Place deep links (#980): /inventory/places/{id} opens that place's page.
// Note the try/catch — assetIdFromPath above decodes bare, so a malformed
// percent-escape throws inside currentRoute and blanks the app on load.
export function placeIdFromPath(pathname: string): EntityReferenceId | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const match = /^\/inventory\/places\/([^/]+)$/.exec(normalized);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1] ?? "");
  } catch {
    return match[1] ?? "";
  }
}

export function placeDetailsPath(placeId: EntityReferenceId): string {
  return `/inventory/places/${encodeURIComponent(String(placeId))}`;
}

/** Food deep links (#1045): /groceries/foods/{id} opens that food's page.
 * try/catch for the same reason placeIdFromPath has one — a malformed
 * percent-escape must degrade, not blank the app on load. */
export function productIdFromPath(pathname: string): EntityReferenceId | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const match = /^\/groceries\/foods\/([^/]+)$/.exec(normalized);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1] ?? "");
  } catch {
    return match[1] ?? "";
  }
}

export function foodDetailsPath(productId: EntityReferenceId): string {
  return `/groceries/foods/${encodeURIComponent(String(productId))}`;
}

export function recipeIdFromPath(pathname: string): EntityReferenceId | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const match = /^\/recipes\/([^/]+)$/.exec(normalized);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1] ?? "");
  } catch {
    return match[1] ?? "";
  }
}

export function recipeDetailsPath(recipeId: EntityReferenceId): string {
  return `/recipes/${encodeURIComponent(String(recipeId))}`;
}

// Assistant chat deep links (#603): /assistant/{uuid} opens that conversation;
// bare /assistant is the new-chat page. The id is the session's public uuid —
// an unknown or foreign value degrades to new-chat in AssistantView.
export function assistantChatIdFromPath(pathname: string): string | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const match = /^\/assistant\/([^/]+)$/.exec(normalized);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1] ?? "");
  } catch {
    return match[1] ?? "";
  }
}

export function assistantChatPath(chatId: string): string {
  return `/assistant/${encodeURIComponent(chatId)}`;
}

export function releaseNotesVersionFromPath(pathname: string): string | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const match = /^\/release-notes\/([^/]+)$/.exec(normalized);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1] ?? "");
  } catch {
    return match[1] ?? "";
  }
}

export function currentRoute(): AppRoute {
  const queryTab = new URLSearchParams(window.location.search).get("tab");
  const normalizedPath = window.location.pathname.replace(/\/+$/, "") || "/";
  if (normalizedPath === "/experiments") {
    navigateBrowser("/", true);
    return route({ tab: "dashboard" });
  }
  const assetId = assetIdFromPath(window.location.pathname);
  const placeId = placeIdFromPath(window.location.pathname);
  const productId = productIdFromPath(window.location.pathname);
  const recipeId = recipeIdFromPath(window.location.pathname);
  const assistantChatId = assistantChatIdFromPath(window.location.pathname);
  const releaseNotesVersion = releaseNotesVersionFromPath(window.location.pathname);
  if (assetId) return route({ tab: "inventory", assetId });
  if (placeId) return route({ tab: "inventory", placeId });
  if (productId) return route({ tab: "groceries", productId });
  if (recipeId) return route({ tab: "recipes", recipeId });
  if (assistantChatId) return route({ tab: "assistant", assistantChatId });
  if (normalizedPath === "/release-notes" || releaseNotesVersion) {
    return route({ tab: "release-notes", releaseNotesVersion });
  }
  if ((normalizedPath === "/" || normalizedPath === "/dashboard") && isTab(queryTab)) {
    return route({ tab: queryTab });
  }

  const pathTab = tabFromPath(window.location.pathname);
  if (pathTab) return route({ tab: pathTab, scanToken: pathTab === "scan" ? scanTokenFromPath(window.location.pathname) : "" });

  if (isTab(queryTab)) return route({ tab: queryTab });

  // Nothing above claimed this URL, so it is not a page of this app (#1259).
  //
  // This used to fall back to a tab remembered in sessionStorage. That read as
  // "resume where you left off", but it could never do that job: tabFromPath
  // already answers for both "/" and "/dashboard" (the Dashboard's own paths),
  // so the fallback was only ever reached by a URL that matched nothing. Its
  // single observable effect was to render /nonsense as Things for a visitor
  // who had last opened Things and as the Dashboard for everyone else, under
  // an address bar still showing /nonsense. The stored tab and its writer went
  // with it; `household.activeTab` had no other reader.
  return route({ tab: "dashboard", notFound: true });
}

export function navigateBrowser(path: string, replace = false) {
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (current === path) return;
  window.history[replace ? "replaceState" : "pushState"](null, "", path);
}

// Clean, canonical path for a route — the deep link we want to share, rather
// than whatever transient query/hash state is on the current URL. It is the
// inverse of `currentRoute`: feed it back through the address bar and the same
// page, showing the same object, comes back.
//
// It used to be a LOSSY inverse (#533). Every id it knew was a path segment, so
// a route whose object lives in the query string — a shopping list, a shopping
// trip, a chore, a project — canonicalised to the bare tab, and the shell's own
// answer to "what URL opens this page" quietly dropped what was on screen.
// `AppRoute.detailIds` closes that: the ids come from `app/detailRoutes.ts`,
// the same table the views write and `entityRoutes.ts` publishes, so a detail
// route added there is canonicalised here without a new field.
//
// It still drops the SUB-SCREEN parameters (`?screen=`, `?view=`, `?tab=`)
// because an AppRoute does not model them, and that is why the copy-link
// affordance in `CommandPalette` deliberately uses `window.location.href`
// rather than this: since #533 every detail view writes its id, so the live
// address already opens what is on screen, and canonicalising it would be a
// downgrade. The one hash it keeps is the Finance section (#1681), which the
// route does model. Nothing else calls this today.
export function canonicalPathForRoute(route: AppRoute): string {
  if (route.assetId) return assetDetailsPath(route.assetId);
  if (route.placeId) return placeDetailsPath(route.placeId);
  if (route.productId) return foodDetailsPath(route.productId);
  if (route.recipeId) return recipeDetailsPath(route.recipeId);
  if (route.tab === "assistant" && route.assistantChatId) return assistantChatPath(route.assistantChatId);
  if (route.tab === "release-notes") {
    return route.releaseNotesVersion
      ? `/release-notes/${encodeURIComponent(route.releaseNotesVersion)}`
      : "/release-notes";
  }
  if (route.tab === "scan") return tabPath("scan", route.scanToken);
  if (route.tab === "finance") return withDetailIds(financeSectionPath(route.financeSection), route);
  return withDetailIds(tabPath(route.tab), route);
}

/**
 * The tab's path with every detail id this route carries appended.
 *
 * A tab may own more than one — shopping carries both `listId` and `tripId`,
 * and a trip opened out of a list is a real state of that page — so they are
 * all kept rather than the first one winning. Order is the detail table's, so
 * the same route always canonicalises to the same string.
 */
function withDetailIds(path: string, route: AppRoute): string {
  const params = new URLSearchParams();
  for (const key of detailRouteKeysForTab(route.tab)) {
    const id = route.detailIds[key];
    if (id) params.set(DETAIL_ROUTES[key].param, id);
  }
  const query = params.toString();
  if (!query) return path;
  // A hash (the Finance section) has to stay last: `/finance#budgets?x=1` is
  // a hash of "budgets?x=1" to the browser.
  const [base, hash] = path.split("#", 2);
  return `${base}?${query}${hash ? `#${hash}` : ""}`;
}

export function canonicalUrlForRoute(route: AppRoute): string {
  return new URL(canonicalPathForRoute(route), window.location.origin).toString();
}
